import { UserObject } from "@/utils/types";
import { useFetchSettings } from "../useFetchSettings";
import { useCustomMutation, useGetData } from "@/hooks/api/use-api";

export const useFetchProfile = (userObject: UserObject, enabled?: boolean) => {
  return useFetchSettings({
    endpoint: "/profile/view",
    queryKeyPrefix: "viewProfile",
    userObject,
    enabled,
  });
};

export const useFetchDisplaySettings = (userObject: UserObject) => {
  return useFetchSettings({
    endpoint: "/profile/settings/display/view",
    queryKeyPrefix: "displaySettings",
    userObject,
  });
};

/** PUT /profile/settings/display/update?theme=&language= — enum query params
 *  (theme: LIGHT|DARK|SYSTEM, language: ENGLISH|FRENCH|SPANISH) alongside a
 *  CredentialRequest body. Call as
 *  mutate({ params: { theme, language }, body: { email, role, usid } }). */
export const useUpdateDisplaySettings = () =>
  useCustomMutation({
    endpoint: "/profile/settings/display/update",
    method: "put",
    useQueryParams: true,
  });

export const useFetchNotificationSettings = (userObject: UserObject) => {
  return useFetchSettings({
    endpoint: "/profile/settings/notification/view",
    queryKeyPrefix: "notificationSettings",
    userObject,
  });
};

/** PUT /profile/settings/notification/update?inAppNotification=&email=&mentioned=
 *  — same query-params-plus-body shape as display settings above. */
export const useUpdateNotificationSettings = () =>
  useCustomMutation({
    endpoint: "/profile/settings/notification/update",
    method: "put",
    useQueryParams: true,
    successMessage: "Notification settings saved",
  });

export const useFetchPrivacyAndSafetySettings = (userObject: UserObject) => {
  return useFetchSettings({
    endpoint: "/profile/settings/privacy-safety/view",
    queryKeyPrefix: "privacyAndSafetySettings",
    userObject,
  });
};

/** POST /profile/settings/privacy-safety/update — body-only, no query params
 *  (UpdatePrivacySettingsRequest: { privacyRequest, credentialRequest }). */
export const useUpdatePrivacySettings = () =>
  useCustomMutation({
    endpoint: "/profile/settings/privacy-safety/update",
    successMessage: "Privacy settings saved",
  });

/** POST /profile/settings/account/view — which of TikTok/X/Facebook/Google
 *  are currently linked. No update endpoint exists in this API for linking
 *  itself (that's presumably a separate OAuth flow), so this is read-only. */
export const useFetchLinkedAccounts = (userObject: UserObject) => {
  return useFetchSettings({
    endpoint: "/profile/settings/account/view",
    queryKeyPrefix: "linkedAccounts",
    userObject,
  });
};

/** GET /profile/preferences/all — the full catalog of selectable interests
 *  (id/name/image each), flat query params rather than a body. */
export const useFetchAllPreferences = (userObject?: UserObject) =>
  useGetData({
    url: `profile/preferences/all?email=${encodeURIComponent(userObject?.email ?? "")}&role=${encodeURIComponent(userObject?.role ?? "")}&usid=${encodeURIComponent(userObject?.usid ?? "")}`,
    queryKey: ["GetAllPreferences", userObject?.email],
    enabled: !!userObject?.email,
  });

/** GET /profile/preferences/my — the subset of the catalog this user has
 *  already selected. */
export const useFetchMyPreferences = (userObject?: UserObject) =>
  useGetData({
    url: `profile/preferences/my?email=${encodeURIComponent(userObject?.email ?? "")}&role=${encodeURIComponent(userObject?.role ?? "")}&usid=${encodeURIComponent(userObject?.usid ?? "")}`,
    queryKey: ["GetMyPreferences", userObject?.email],
    enabled: !!userObject?.email,
  });

/** POST /profile/preferences/add — body: UserPreferenceRequest
 *  { credentialRequest, preferenceIds: number[] }. */
export const useAddPreferences = () =>
  useCustomMutation({
    endpoint: "/profile/preferences/add",
  });

/** DELETE /profile/preferences/remove — same body shape as add, above. */
export const useRemovePreferences = () =>
  useCustomMutation({
    endpoint: "/profile/preferences/remove",
    method: "delete",
  });

export const useFetchAgoraRTCToken = (uid: string, channelName: string) => {
  return useFetchSettings({
    endpoint: `/agora/rtc-token?channel=${channelName}&uid=${uid}`,
    queryKeyPrefix: "fetchAgoraRTCToken",
  });
};
